import React from 'react';
import { FiMail, FiPhone, FiMapPin, FiMessageCircle } from 'react-icons/fi';

export const TermsPage = () => (
  <div className="section-padding">
    <div className="container fd-page-container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Terms of Service</h1>
      <p className="text-muted mb-4">Last updated: February 2026</p>

      <div className="dashboard-card mb-4">
        <h5>1. Acceptance of Terms</h5>
        <p>By accessing and using FarmDirectly, you agree to be bound by these Terms of Service. If you do not agree, please do not use our platform.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>2. User Accounts</h5>
        <p>You are responsible for maintaining the confidentiality of your account credentials. You must provide accurate information during registration and keep your account information up to date.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>3. Merchant Responsibilities</h5>
        <p>Merchants are responsible for the accuracy of product listings, timely order fulfillment, and compliance with applicable laws. FarmDirectly reserves the right to suspend accounts that violate our policies.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>4. Purchases & Payments</h5>
        <p>All purchases are subject to product availability. Prices are displayed in USD unless otherwise noted. Payments are processed securely through Stripe and Razorpay.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>5. Returns & Refunds</h5>
        <p>Return policies are set by individual merchants. Please review each merchant's return policy before purchasing. Disputes should be raised within 14 days of delivery.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>6. Limitation of Liability</h5>
        <p>FarmDirectly acts as a marketplace platform and is not directly responsible for products sold by merchants. We strive to ensure a safe shopping experience but cannot guarantee the quality of third-party products.</p>
      </div>
    </div>
  </div>
);

export const PrivacyPage = () => (
  <div className="section-padding">
    <div className="container fd-page-container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Privacy Policy</h1>
      <p className="text-muted mb-4">Last updated: February 2026</p>

      <div className="dashboard-card mb-4">
        <h5>Information We Collect</h5>
        <p>We collect information you provide directly: name, email, shipping address, payment details (processed by Stripe/Razorpay — we do not store card numbers), and browsing activity on our platform.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>How We Use Your Information</h5>
        <p>Your information is used to process orders, provide customer support, improve our platform, send transactional emails (order confirmations, shipping updates), and personalize your experience.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Data Sharing</h5>
        <p>We share necessary information with merchants to fulfill orders, and with payment processors for transactions. We do not sell your personal data to third parties.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Cookies & Tracking</h5>
        <p>We use cookies for authentication, preferences (language, theme), and recently viewed products. You can manage cookie preferences in your browser settings.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Your Rights</h5>
        <p>You can access, update, or delete your account data from your profile page. For data deletion requests, contact us at privacy@farmdirectly.com.</p>
      </div>
    </div>
  </div>
);

export const ContactPage = () => (
  <div className="section-padding">
    <div className="container fd-page-container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Contact Us</h1>
      <p className="text-muted mb-4">We'd love to hear from you. Reach out through any of these channels.</p>

      <div className="row g-4">
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiMail size={32} className="text-primary mb-3" />
            <h5>Email</h5>
            <p className="text-muted">support@farmdirectly.com</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiPhone size={32} className="text-primary mb-3" />
            <h5>Phone</h5>
            <p className="text-muted">+91 44 1234 5678</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiMapPin size={32} className="text-primary mb-3" />
            <h5>Address</h5>
            <p className="text-muted">Tiruvottiyūr, Chennai<br />Tamil Nadu, India</p>
          </div>
        </div>
        <div className="col-md-6">
          <div className="dashboard-card h-100 text-center">
            <FiMessageCircle size={32} className="text-primary mb-3" />
            <h5>Business Hours</h5>
            <p className="text-muted">Mon – Sat: 9 AM – 6 PM IST<br />Sunday: Closed</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const HelpPage = () => (
  <div className="section-padding">
    <div className="container fd-page-container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Help Center</h1>

      <div className="dashboard-card mb-3">
        <h5>How do I place an order?</h5>
        <p className="text-muted mb-0">Browse products, add items to your cart, proceed to checkout, enter your shipping details, and complete payment via Stripe or Razorpay.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I track my order?</h5>
        <p className="text-muted mb-0">Go to "My Orders" from your account menu. Click on any order to see the tracking timeline with real-time status updates and tracking numbers.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I apply a coupon?</h5>
        <p className="text-muted mb-0">Enter your coupon code in the "Coupon code" field on the cart page and click "Apply". The discount will be reflected in your order summary.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do loyalty points work?</h5>
        <p className="text-muted mb-0">You earn 1 point for every $1 spent. Once you have 100+ points, you can redeem them at checkout — 100 points = $1.00 discount.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I become a seller?</h5>
        <p className="text-muted mb-0">Register with a merchant account, set up your store in the Store Settings page, and start adding products. Your store will be activated after admin approval.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I return a product?</h5>
        <p className="text-muted mb-0">Return policies vary by merchant. Check the product page or order details for the specific return policy. Contact the merchant directly for return requests.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I bulk import products?</h5>
        <p className="text-muted mb-0">Merchants can go to "Bulk Import" in the sidebar, download the CSV template, fill in your products, and upload. All imported products start as drafts.</p>
      </div>

      <div className="text-center mt-4">
        <p className="text-muted">Still have questions?</p>
        <a href="/contact" className="btn btn-fd-primary">Contact Support</a>
      </div>
    </div>
  </div>
);

export const AboutPage = () => (
  <div className="section-padding">
    <div className="container fd-page-container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">About FarmDirectly</h1>

      <div className="dashboard-card mb-4">
        <h5>Our Mission</h5>
        <p>FarmDirectly is India's farming-focused multi-vendor farm-to-doorstep marketplace. We connect farmers, agricultural suppliers, and rural entrepreneurs directly with consumers — eliminating middlemen and ensuring fair prices for everyone.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>What We Do</h5>
        <p>We provide a platform where thousands of verified merchants can sell fresh produce, organic fertilizers, seeds, farm equipment, and traditional products. Our technology enables real-time inventory management, secure payments via Stripe and Razorpay, and reliable delivery across India.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Why FarmDirectly?</h5>
        <p><strong>For Buyers:</strong> Access fresh, farm-direct products at competitive prices. Earn loyalty points, use gift cards, and enjoy personalized recommendations.</p>
        <p><strong>For Sellers:</strong> List unlimited products, access powerful analytics, create coupons, and reach millions of customers with zero upfront costs.</p>
        <p><strong>For Communities:</strong> We support local economies by empowering small farmers and rural businesses with digital commerce tools.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Our Technology</h5>
        <p>Built on a modern MERN stack (MongoDB, Express, React, Node.js), FarmDirectly features 21 specialized user roles, real-time chat, multi-language support (English & Tamil), flash deals, product recommendations, and a comprehensive admin dashboard.</p>
      </div>

      <div className="dashboard-card mb-4">
        <h5>Contact Us</h5>
        <p>Have questions? Reach us at <a href="mailto:support@farmdirectly.com">support@farmdirectly.com</a> or visit our <a href="/contact">Contact page</a>.</p>
      </div>
    </div>
  </div>
);

export const FAQPage = () => (
  <div className="section-padding">
    <div className="container fd-page-container" style={{ maxWidth: '800px' }}>
      <h1 className="mb-4">Frequently Asked Questions</h1>

      <div className="dashboard-card mb-3">
        <h5>How do I create an account?</h5>
        <p>Click "Sign Up" in the top right corner, choose your role (Customer, Merchant, or Affiliate Partner), and fill in your details. Verification email will be sent to confirm your account.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I place an order?</h5>
        <p>Browse products, add items to your cart, proceed to checkout, enter your shipping address, select a payment method (Stripe, Razorpay, or Cash on Delivery), and confirm your order.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>What payment methods are accepted?</h5>
        <p>We accept credit/debit cards via Stripe, UPI/cards/netbanking/wallets via Razorpay, and Cash on Delivery for eligible orders.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I become a seller?</h5>
        <p>Register as a Merchant, set up your store profile, complete KYC verification, and start listing your products. There are no upfront fees — we only charge a small commission on completed sales.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How does the affiliate program work?</h5>
        <p>Register as an Affiliate Partner, get unique referral links for any product, share them with your audience, and earn commissions on every sale made through your links.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>Can I return or exchange products?</h5>
        <p>Yes, most products are eligible for returns within 7 days of delivery. Go to your order details and initiate a return request. Our returns team will process it within 2-3 business days.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do gift cards work?</h5>
        <p>Purchase a gift card from the Gift Cards page, send it to anyone via email, and they can redeem it during checkout. Gift card balances never expire.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>Is FarmDirectly available in Tamil?</h5>
        <p>Yes! Click the globe icon (🌐) in the top navigation to switch between English and Tamil. Product names, descriptions, and all UI elements are translated.</p>
      </div>

      <div className="dashboard-card mb-3">
        <h5>How do I contact support?</h5>
        <p>Use the chat widget on any product page to message the seller directly, or email us at <a href="mailto:support@farmdirectly.com">support@farmdirectly.com</a> for platform-related issues.</p>
      </div>
    </div>
  </div>
);
